import type { PeerStats } from './api';

/** One entry of an RTCStatsReport. Fields vary by `type`, so they are read defensively. */
export interface RtcStat {
  id: string;
  type: string;
  timestamp?: number;
  [key: string]: unknown;
}

/** The cumulative counters and gauges of one getStats() call that PeerStats is derived from. */
export interface StatsSample {
  timestampMs: number;
  bytesSent: number;
  bytesReceived: number;
  videoCodec: string | null;
  audioCodec: string | null;
  candidateType: string | null;
  /** True when the transport reports a selected (or nominated and succeeded) candidate pair. */
  hasSelectedPair: boolean;
  availableOutgoingBitrate: number | null;
  rttSec: number | null;
  /** 0..1 as reported by the remote receiver for our outbound video. */
  remoteFractionLost: number | null;
  /** Receive-side video counters, used when this end is not sending video. */
  packetsReceived: number;
  packetsLost: number;
  fps: number | null;
  frameWidth: number | null;
  frameHeight: number | null;
  qualityLimitationReason: string | null;
  nackCount: number;
  pliCount: number;
  /** Cumulative send counters; null on an end that does not send that kind. */
  framesSent: number | null;
  framesEncoded: number | null;
  audioPacketsSent: number | null;
  /** e.g. 'libvpx', 'VideoToolbox', 'ExternalEncoder'; null on an end that does not send video. */
  encoderImplementation: string | null;
  powerEfficientEncoder: boolean | null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function kindOf(stat: RtcStat): string | null {
  return text(stat.kind) ?? text(stat.mediaType);
}

export function sampleStats(stats: Iterable<RtcStat>, nowMs: number): StatsSample {
  const byId = new Map<string, RtcStat>();
  for (const stat of stats) byId.set(stat.id, stat);

  const sample: StatsSample = {
    timestampMs: nowMs,
    bytesSent: 0,
    bytesReceived: 0,
    videoCodec: null,
    audioCodec: null,
    candidateType: null,
    hasSelectedPair: false,
    availableOutgoingBitrate: null,
    rttSec: null,
    remoteFractionLost: null,
    packetsReceived: 0,
    packetsLost: 0,
    fps: null,
    frameWidth: null,
    frameHeight: null,
    qualityLimitationReason: null,
    nackCount: 0,
    pliCount: 0,
    framesSent: null,
    framesEncoded: null,
    audioPacketsSent: null,
    encoderImplementation: null,
    powerEfficientEncoder: null,
  };

  const codecOf = (stat: RtcStat): string | null => {
    const codec = typeof stat.codecId === 'string' ? byId.get(stat.codecId) : undefined;
    return codec ? text(codec.mimeType) : null;
  };

  let latest = 0;
  let sendsVideo = false;
  let remoteRtt: number | null = null;
  let selectedPair: RtcStat | undefined;
  let nominatedPair: RtcStat | undefined;

  for (const stat of byId.values()) {
    const kind = kindOf(stat);
    switch (stat.type) {
      case 'outbound-rtp': {
        latest = Math.max(latest, num(stat.timestamp) ?? 0);
        sample.bytesSent += num(stat.bytesSent) ?? 0;
        if (kind === 'video') {
          sendsVideo = true;
          sample.videoCodec = codecOf(stat) ?? sample.videoCodec;
          sample.fps = num(stat.framesPerSecond) ?? sample.fps;
          sample.frameWidth = num(stat.frameWidth) ?? sample.frameWidth;
          sample.frameHeight = num(stat.frameHeight) ?? sample.frameHeight;
          sample.qualityLimitationReason = text(stat.qualityLimitationReason) ?? sample.qualityLimitationReason;
          sample.nackCount += num(stat.nackCount) ?? 0;
          sample.pliCount += num(stat.pliCount) ?? 0;
          sample.framesSent = (sample.framesSent ?? 0) + (num(stat.framesSent) ?? 0);
          sample.framesEncoded = (sample.framesEncoded ?? 0) + (num(stat.framesEncoded) ?? 0);
          sample.encoderImplementation = text(stat.encoderImplementation) ?? sample.encoderImplementation;
          if (typeof stat.powerEfficientEncoder === 'boolean') sample.powerEfficientEncoder = stat.powerEfficientEncoder;
        } else if (kind === 'audio') {
          sample.audioCodec = codecOf(stat) ?? sample.audioCodec;
          sample.audioPacketsSent = (sample.audioPacketsSent ?? 0) + (num(stat.packetsSent) ?? 0);
        }
        break;
      }
      case 'inbound-rtp': {
        latest = Math.max(latest, num(stat.timestamp) ?? 0);
        sample.bytesReceived += num(stat.bytesReceived) ?? 0;
        if (kind === 'video') {
          sample.packetsReceived += num(stat.packetsReceived) ?? 0;
          sample.packetsLost += Math.max(0, num(stat.packetsLost) ?? 0);
        }
        break;
      }
      case 'remote-inbound-rtp': {
        if (kind === 'video') sample.remoteFractionLost = num(stat.fractionLost) ?? sample.remoteFractionLost;
        remoteRtt = num(stat.roundTripTime) ?? remoteRtt;
        break;
      }
      case 'transport': {
        const pair = typeof stat.selectedCandidatePairId === 'string' ? byId.get(stat.selectedCandidatePairId) : undefined;
        if (pair) selectedPair = pair;
        break;
      }
      case 'candidate-pair': {
        if (stat.nominated === true && stat.state === 'succeeded') nominatedPair = stat;
        break;
      }
    }
  }

  // A receive-only end has no outbound video; describe what it is receiving instead.
  if (!sendsVideo) {
    for (const stat of byId.values()) {
      if (stat.type !== 'inbound-rtp') continue;
      const kind = kindOf(stat);
      if (kind === 'video') {
        sample.videoCodec = codecOf(stat) ?? sample.videoCodec;
        sample.fps = num(stat.framesPerSecond) ?? sample.fps;
        sample.frameWidth = num(stat.frameWidth) ?? sample.frameWidth;
        sample.frameHeight = num(stat.frameHeight) ?? sample.frameHeight;
        sample.nackCount += num(stat.nackCount) ?? 0;
        sample.pliCount += num(stat.pliCount) ?? 0;
      } else if (kind === 'audio' && sample.audioCodec === null) {
        sample.audioCodec = codecOf(stat);
      }
    }
  }

  const pair = selectedPair ?? nominatedPair;
  if (pair) {
    sample.hasSelectedPair = true;
    sample.availableOutgoingBitrate = num(pair.availableOutgoingBitrate);
    sample.rttSec = num(pair.currentRoundTripTime);
    const local = typeof pair.localCandidateId === 'string' ? byId.get(pair.localCandidateId) : undefined;
    sample.candidateType = local ? text(local.candidateType) : null;
  }
  sample.rttSec ??= remoteRtt;
  if (latest > 0) sample.timestampMs = latest;
  return sample;
}

function kbps(previous: number, current: number, seconds: number): number {
  // Counters restart from zero when a connection is rebuilt; a negative delta is not a rate.
  if (seconds <= 0 || current < previous) return 0;
  return ((current - previous) * 8) / 1000 / seconds;
}

export function computePeerStats(
  previous: StatsSample | null,
  current: StatsSample,
  connectionState: RTCPeerConnectionState,
): PeerStats {
  const seconds = previous ? (current.timestampMs - previous.timestampMs) / 1000 : 0;

  let packetLossPct: number | null = null;
  if (current.remoteFractionLost !== null) {
    packetLossPct = Math.min(100, Math.max(0, current.remoteFractionLost * 100));
  } else if (previous) {
    const lost = current.packetsLost - previous.packetsLost;
    const received = current.packetsReceived - previous.packetsReceived;
    if (lost >= 0 && received >= 0 && lost + received > 0) packetLossPct = (lost / (lost + received)) * 100;
  }

  return {
    connectionState,
    candidateType: current.candidateType,
    videoCodec: current.videoCodec,
    audioCodec: current.audioCodec,
    outKbps: previous ? kbps(previous.bytesSent, current.bytesSent, seconds) : 0,
    inKbps: previous ? kbps(previous.bytesReceived, current.bytesReceived, seconds) : 0,
    availableOutKbps: current.availableOutgoingBitrate === null ? null : current.availableOutgoingBitrate / 1000,
    rttMs: current.rttSec === null ? null : current.rttSec * 1000,
    packetLossPct,
    fps: current.fps,
    frameWidth: current.frameWidth,
    frameHeight: current.frameHeight,
    qualityLimitationReason: current.qualityLimitationReason,
    nackCount: current.nackCount,
    pliCount: current.pliCount,
    framesSent: current.framesSent,
    framesEncoded: current.framesEncoded,
    audioPacketsSent: current.audioPacketsSent,
    encoderImplementation: current.encoderImplementation,
    powerEfficientEncoder: current.powerEfficientEncoder,
    // Filled in by the host from the peer's data-channel reports; nothing else has one.
    receiver: null,
  };
}

/** Rates need two samples a meaningful time apart; closer calls reuse the older baseline. */
const MIN_WINDOW_MS = 500;

export interface StatsWindow {
  push(sample: StatsSample, connectionState: RTCPeerConnectionState): PeerStats;
  reset(): void;
}

export function createStatsWindow(): StatsWindow {
  let baseline: StatsSample | null = null;
  let latest: StatsSample | null = null;
  return {
    push(sample, connectionState) {
      if (latest === null) latest = sample;
      else if (sample.timestampMs - latest.timestampMs >= MIN_WINDOW_MS) {
        baseline = latest;
        latest = sample;
      }
      const reference = latest === sample ? baseline : (baseline ?? latest);
      return computePeerStats(reference, sample, connectionState);
    },
    reset() {
      baseline = latest = null;
    },
  };
}

export async function readPeerStats(pc: RTCPeerConnection, window: StatsWindow): Promise<PeerStats> {
  const report = await pc.getStats();
  const stats: RtcStat[] = [];
  report.forEach((stat: RtcStat) => stats.push(stat));
  return window.push(sampleStats(stats, Date.now()), pc.connectionState);
}
